"use client"

import { CalendarRange, Clock3, Database, RotateCcw, SlidersHorizontal, Target } from "lucide-react"

import { Button } from "@/common/components/ui/button"
import { Label } from "@/common/components/ui/label"
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/common/components/ui/select"
import { Slider } from "@/common/components/ui/slider"
import { Switch } from "@/common/components/ui/switch"
import { cn } from "@/common/lib/utils"
import { ASPIRATIONS } from "@/modules/lernpfad/data/roles"
import type { SkillSource } from "@/modules/lernpfad/engine"
import type { LernpfadDemoState } from "@/app/demo/lernpfad/use-lernpfad-demo"

const SOURCE_KEYS: SkillSource[] = [
    "stellenprofil",
    "lernhistorie",
    "cv",
    "mag",
    "selbsteinschaetzung",
]

/**
 * The live levers.
 *
 * Each one re-runs the engine (see `use-lernpfad-demo.ts`), so every question
 * from the room is answerable in place. The `mag` switch under "data sources"
 * is the one that matters most in an HR audience: switching performance data
 * off and watching a valid plan survive is the live answer to the works-council
 * objection, rather than an argument about it.
 */
export default function DemoLevers({ state }: { state: LernpfadDemoState }) {
    const {
        employee,
        baseEmployee,
        pensumOverride,
        setPensumOverride,
        hoursPerQuarter,
        setHoursPerQuarter,
        aspirationOverride,
        setAspirationOverride,
        monthOffset,
        setMonthOffset,
        horizonMonths,
        setHorizonMonths,
        allowedSources,
        toggleSource,
        leversTouched,
        reset,
        asOf,
        curriculum,
        localizer,
        persona,
        moodleConnected,
    } = state
    const { t } = localizer

    // Pensum and career goal describe ONE person, so they are meaningless on the
    // population view — showing them there would invite a question the number
    // cannot answer.
    const perPerson = persona === "mitarbeitende"

    return (
        <div className="lp-card overflow-hidden rounded-xl border bg-card">
            <div className="flex items-center justify-between border-b bg-muted/40 px-4 py-2.5">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <SlidersHorizontal className="size-3.5" />
                    {t("levers.title")}
                </h3>
                {leversTouched && (
                    <Button size="sm" variant="ghost" onClick={reset} className="h-7 text-xs">
                        <RotateCcw className="size-3" />
                        {t("levers.reset")}
                    </Button>
                )}
            </div>

            <div className="space-y-5 p-4">
                {perPerson && (
                    <>
                        {/* Pensum — the demo's single most persuasive control. */}
                        <div className="space-y-2">
                            <div className="flex items-baseline justify-between">
                                <Label className="text-xs">{t("levers.pensum")}</Label>
                                <span className="text-sm font-semibold tabular-nums">
                                    {employee.pensum}%
                                    {pensumOverride !== null &&
                                        pensumOverride !== baseEmployee.pensum && (
                                            <span className="ml-1 text-xs font-normal text-muted-foreground">
                                                ({t("levers.pensumReal", { value: baseEmployee.pensum })})
                                            </span>
                                        )}
                                </span>
                            </div>
                            <Slider
                                min={20}
                                max={100}
                                step={10}
                                value={[employee.pensum]}
                                onValueChange={([value]) => setPensumOverride(value)}
                            />
                            <p className="rounded-md bg-primary/5 px-2 py-1 text-xs text-muted-foreground">
                                {t("levers.budget", { hours: curriculum.budgetHoursPerQuarter })}
                            </p>
                        </div>

                        {/* Aspiration */}
                        <div className="space-y-2">
                            <Label className="flex items-center gap-1.5 text-xs">
                                <Target className="size-3" />
                                {t("levers.aspiration")}
                            </Label>
                            <Select
                                value={aspirationOverride ?? baseEmployee.aspiration ?? "none"}
                                onValueChange={setAspirationOverride}
                            >
                                <SelectTrigger className="w-full">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    {ASPIRATIONS.map((aspiration) => (
                                        <SelectItem key={aspiration.id} value={aspiration.id}>
                                            {localizer.aspiration(aspiration.id, aspiration.label)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </>
                )}

                {/* Time budget */}
                <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                        <Label className="flex items-center gap-1.5 text-xs">
                            <Clock3 className="size-3" />
                            {t("levers.timeBudget")}
                        </Label>
                        <span className="text-sm font-semibold tabular-nums">
                            {t("levers.hoursPerQuarter", { hours: hoursPerQuarter })}
                        </span>
                    </div>
                    <Slider
                        min={4}
                        max={40}
                        step={2}
                        value={[hoursPerQuarter]}
                        onValueChange={([value]) => setHoursPerQuarter(value)}
                    />
                </div>

                {/* Reference date — moves refreshers into expiry */}
                <div className="space-y-2">
                    <div className="flex items-baseline justify-between">
                        <Label className="flex items-center gap-1.5 text-xs">
                            <CalendarRange className="size-3" />
                            {t("levers.asOf")}
                        </Label>
                        <span className="text-sm font-semibold tabular-nums">
                            {localizer.date(asOf)}
                        </span>
                    </div>
                    <Slider
                        min={0}
                        max={24}
                        step={1}
                        value={[monthOffset]}
                        onValueChange={([value]) => setMonthOffset(value)}
                    />
                    <p
                        className={cn(
                            "text-xs",
                            monthOffset === 0 ? "text-muted-foreground" : "font-medium text-amber-600",
                        )}
                    >
                        {monthOffset === 0
                            ? t("levers.today")
                            : t("levers.monthsAhead", { months: monthOffset })}
                    </p>
                </div>

                {/* Horizon */}
                <div className="space-y-2">
                    <Label className="text-xs">{t("levers.horizon")}</Label>
                    <Select
                        value={String(horizonMonths)}
                        onValueChange={(value) => setHorizonMonths(Number(value))}
                    >
                        <SelectTrigger className="w-full">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {[6, 12, 24].map((months) => (
                                <SelectItem key={months} value={String(months)}>
                                    {t("levers.months", { months })}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Data sources — the governance lever */}
                <div className="space-y-3 rounded-lg border bg-muted/25 p-3">
                    <div>
                        <Label className="flex items-center gap-1.5 text-xs font-semibold">
                            <Database className="size-3" />
                            {t("levers.sources")}
                        </Label>
                        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                            {t("levers.sourcesHint")}
                        </p>
                    </div>
                    {moodleConnected && (
                        <div className="flex items-center justify-between gap-2 border-b pb-2">
                            <span className="text-sm">
                                {t("levers.moodleTranscript")}
                                <span className="ml-1.5 block text-xs text-muted-foreground">
                                    {t("levers.moodleTranscriptHint")}
                                </span>
                            </span>
                            <Switch checked disabled />
                        </div>
                    )}
                    {SOURCE_KEYS.map((source) => (
                        <div key={source} className="flex items-center justify-between gap-2">
                            <span className="text-sm">
                                {t(`source.${source}` as never)}
                                {source === "mag" && (
                                    <span className="ml-1.5 rounded border border-amber-500/40 px-1 py-px text-xs text-amber-700 dark:text-amber-400">
                                        {t("levers.sensitive")}
                                    </span>
                                )}
                            </span>
                            <Switch
                                checked={allowedSources.includes(source)}
                                onCheckedChange={() => toggleSource(source)}
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    )
}
